/**
 * SystemPage — host-süsteemi info + reboot/restart-ssh nupud + web terminal.
 *
 * Ainult admin'ile. Backend (api/system.js) kasutab host-bridge containerit
 * privileged host käskude (reboot, systemctl restart ssh) käivitamiseks.
 *
 * Copyright (c) 2026 Rohepaneel
 */
import { useState, useEffect, useCallback } from 'react';
import { Header } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Power, RefreshCw, Terminal, Wrench, AlertOctagon, Activity, Cpu, MemoryStick } from 'lucide-react';

function fmtBytes(n) {
  if (n == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}
function fmtUptime(sec) {
  if (sec == null) return '—';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return `${m}m ${s}s`;
}

function StatCard({ icon, label, value }) {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 6, padding: '14px 16px', flex: 1, minWidth: 180,
    }}>
      <div style={{
        fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em',
        color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
      }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)' }}>{value}</div>
    </div>
  );
}

function ActionButton({ icon, label, description, color, onClick, busy, confirm }) {
  const handleClick = () => {
    if (confirm && !window.confirm(confirm)) return;
    onClick();
  };
  return (
    <button onClick={handleClick} disabled={busy} style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '14px 16px', borderRadius: 6,
      background: 'var(--surface)', border: `1px solid ${color}40`,
      cursor: busy ? 'wait' : 'pointer', textAlign: 'left',
      transition: 'all 0.15s ease', minWidth: 280,
      opacity: busy ? 0.6 : 1,
    }}
    onMouseEnter={(e) => !busy && (e.currentTarget.style.background = `${color}10`)}
    onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--surface)')}
    >
      <div style={{ color, fontSize: 20, marginTop: 2 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '0.88rem', fontWeight: 600, color }}>{label}</div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-3)', marginTop: 2 }}>{description}</div>
      </div>
    </button>
  );
}

export function SystemPage({ wsConnected }) {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [msg, setMsg] = useState(null);

  const loadInfo = useCallback(async () => {
    try {
      const r = await fetch('/api/system/info');
      if (!r.ok) throw new Error(`${r.status}`);
      const d = await r.json();
      setInfo(d);
      setErr(null);
    } catch (e) {
      setErr(`Info ei laadita: ${e.message}`);
    }
  }, []);

  useEffect(() => {
    loadInfo();
    const iv = setInterval(loadInfo, 5000);
    return () => clearInterval(iv);
  }, [loadInfo]);

  const triggerAction = async (path, label) => {
    setBusyAction(label);
    setMsg(null);
    try {
      const r = await fetch(path, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `${r.status}`);
      setMsg({ type: 'ok', text: `${label}: ${d.message || 'OK'}` });
    } catch (e) {
      setMsg({ type: 'err', text: `${label} ebaõnnestus: ${e.message}` });
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--bg)' }}>
      <Header title="Süsteem" connected={wsConnected} />
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px 40px', maxWidth: 1480, width: '100%', margin: '0 auto' }}>

        {/* Süsteemi info */}
        <h2 style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: 10 }}>
          Süsteemi seisund
        </h2>
        {err && (
          <div style={{ padding: 10, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 4, color: '#b91c1c', marginBottom: 14, fontSize: '0.8rem' }}>
            {err}
          </div>
        )}
        {info && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
            <StatCard icon={<Cpu size={11} />} label="Hostname" value={info.hostname} />
            <StatCard icon={<Activity size={11} />} label="Uptime" value={fmtUptime(info.uptime_sec)} />
            <StatCard icon={<Activity size={11} />} label="Load avg" value={info.load_avg.map(v => v.toFixed(2)).join(' / ')} />
            <StatCard icon={<Cpu size={11} />} label="CPU" value={`${info.cpu_count} cores · ${info.arch}`} />
            <StatCard icon={<MemoryStick size={11} />} label="Mälu vaba"
              value={`${fmtBytes(info.free_mem_bytes)} / ${fmtBytes(info.total_mem_bytes)}`} />
            <StatCard icon={<Activity size={11} />} label="Backend uptime" value={fmtUptime(info.backend_uptime_sec)} />
          </div>
        )}

        {/* Tegevus-nupud */}
        <h2 style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: 10 }}>
          Süsteemi haldus
        </h2>
        {msg && (
          <div style={{
            padding: 10, marginBottom: 14, fontSize: '0.8rem', borderRadius: 4,
            background: msg.type === 'ok' ? 'rgba(21,128,61,0.08)' : 'rgba(220,38,38,0.08)',
            border: `1px solid ${msg.type === 'ok' ? 'rgba(21,128,61,0.3)' : 'rgba(220,38,38,0.3)'}`,
            color: msg.type === 'ok' ? '#15803d' : '#b91c1c',
          }}>
            {msg.text}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 30 }}>
          <ActionButton
            icon={<Wrench size={20} />}
            label="Parandus: systemd-resolved"
            description="Mask resolved + restart ssh — kui SSH banner timeout-ub"
            color="#1d4ed8"
            busy={busyAction === 'fix-resolved'}
            onClick={() => triggerAction('/api/system/fix-resolved', 'fix-resolved')}
          />
          <ActionButton
            icon={<RefreshCw size={20} />}
            label="Restart SSH"
            description="systemctl restart ssh hostil"
            color="#0891b2"
            busy={busyAction === 'restart-ssh'}
            onClick={() => triggerAction('/api/system/restart-ssh', 'restart-ssh')}
          />
          <ActionButton
            icon={<RefreshCw size={20} />}
            label="Restart Docker"
            description="systemctl restart docker — taaskäivitab kõik konteinerid"
            color="#7c3aed"
            busy={busyAction === 'restart-docker'}
            confirm="Kindel? Kõik containerid (k.a backend, frontend) restartivad ~30s. Sa kaotad veebivõrgu hetkeks."
            onClick={() => triggerAction('/api/system/restart-docker', 'restart-docker')}
          />
          <ActionButton
            icon={<Power size={20} />}
            label="Restart server"
            description="reboot — host-süsteem taaskäivitub täielikult ~30-60s"
            color="#dc2626"
            busy={busyAction === 'reboot'}
            confirm="KINDEL? Serveriviib reboot, kõik teenused alla ~30-60s. Sa kaotad ligipääsu kuni server tagasi tuleb."
            onClick={() => triggerAction('/api/system/reboot', 'reboot')}
          />
        </div>

        {/* Web terminal */}
        <h2 style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Terminal size={14} /> Web terminal (bash shell)
        </h2>
        <div style={{
          background: '#1e293b', border: '1px solid var(--border)',
          borderRadius: 6, overflow: 'hidden', height: 480,
        }}>
          <iframe
            src="/console/"
            title="Web terminal"
            style={{ width: '100%', height: '100%', border: 0 }}
            allow="clipboard-read; clipboard-write"
          />
        </div>
        <div style={{ marginTop: 8, fontSize: '0.7rem', color: 'var(--text-3)' }}>
          Terminal jookseb privileged ttyd containeris, pid:host — täielik ligipääs host-süsteemile.
          Auth kontrollitakse iga päringuga nginx-i kaudu.
        </div>
      </div>
    </div>
  );
}

export default SystemPage;
